import React, { Ref, useCallback, useEffect, useRef, useState } from 'react'

import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client'
import classNames from 'classnames'

import { CommonDeleteIcon, CommonEditIcon, CommonSuspendedIcon } from '@local/assets/icons'
import { ClipEncodingImage } from '@local/assets/images'
import { getRegionName } from '@local/utils'

import Clickable from '@web/components/Clickable'
import useDeleteClipModal from '@web/components/ClipCard/DeleteClipModal'
import ContextMenu from '@web/components/ContextMenu/ContextMenu'
import Image from '@web/components/Image'
import FallbackImage from '@web/components/Image/FallbackImage'
import { gql } from '@web/gql'
import { ClipEncodingStatus, ClipItemCard_UserClipsFragment } from '@web/gql/graphql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { useGoTo } from '@web/hooks/useGoTo'
import useGoToClip from '@web/hooks/useGoToClip'
import useMe from '@web/hooks/useMe'
import usePageInfo from '@web/hooks/usePageInfo'
import nClicks from '@web/libs/nlog/click/codes'

import styles from './ClipCard.module.scss'

interface Props {
  clip: ClipItemCard_UserClipsFragment
}

export const ClipMetaQuery = gql(`
  query ClipMeta($videoId: String!) {
    clipMeta(videoId: $videoId)
  }
`)

export const ClipStatusQuery = gql(`
  query ClipStatus($videoId: String!) {
    clipStatus(videoId: $videoId)
  }
`)

const ClipCard = (props: Props, ref: Ref<HTMLLIElement>) => {
  const { login } = useMe()
  const { isMyPage } = usePageInfo()
  const { isFromNaverApp } = useGlobalStaticStateContext()
  const { goToLogin, goToClipEdit } = useGoTo()
  const { goToClipViewer } = useGoToClip()
  const { clip } = props
  const { thumbnail } = clip
  const [encodingStatus, setEncodingStatus] = useState(clip.encodingStatusCode)
  const [isEditing, setIsEditing] = useState<boolean>(false)

  const client = useApolloClient()

  const nClickCodes = {
    edit: nClicks.reviewTab.CLIP_EDIT,
    delete: nClicks.reviewTab.CLIP_DEL,
    deleteNo: nClicks.reviewTab.CLIP_DEL_X,
    deleteYes: nClicks.reviewTab.CLIP_DEL_O,
  }

  const { openModal: openDeleteModal } = useDeleteClipModal({ videoId: clip.videoId, nClickCodes })

  const pollingCount = useRef(0)

  const [getClipMeta] = useLazyQuery(ClipMetaQuery, {
    variables: { videoId: clip.videoId },
    fetchPolicy: 'network-only',
  })

  const { stopPolling } = useQuery(ClipStatusQuery, {
    variables: { videoId: clip.videoId },
    notifyOnNetworkStatusChange: true,
    skip: clip.encodingStatusCode !== ClipEncodingStatus.Progress,
    pollInterval: 5000,
    fetchPolicy: 'network-only',
    onCompleted: data => {
      pollingCount.current += 1

      if (data.clipStatus === ClipEncodingStatus.Complete || pollingCount.current > 60) {
        stopPolling()
        setEncodingStatus(
          data.clipStatus === ClipEncodingStatus.Complete
            ? ClipEncodingStatus.Complete
            : ClipEncodingStatus.CheckSource,
        )
      }
    },
    onError: () => {
      stopPolling()
    },
  })

  const isEncoding = encodingStatus === ClipEncodingStatus.Progress
  const isEncodingFailed =
    encodingStatus === ClipEncodingStatus.CheckSource || encodingStatus === ClipEncodingStatus.CheckAdmin
  const typeEncoding = isEncoding || isEncodingFailed
  const typeBlocked = clip.blockedYn
  const isImageBlocked = clip.imageBlockedYn

  const menuItems = [
    {
      label: '수정하기',
      icon: CommonEditIcon,
      clickHandler: () => handleClickEdit(),
      visibility: isFromNaverApp && !typeEncoding,
      clickCode: nClickCodes.edit,
    },
    {
      label: '삭제하기',
      icon: CommonDeleteIcon,
      clickHandler: () => handleClickDelete(),
      visibility: isMyPage && !isEncoding,
      clickCode: nClickCodes.delete,
    },
  ]

  const handleClickEdit = async () => {
    if (!login) {
      goToLogin()
      return null
    }

    const { data } = await getClipMeta()
    if (!data?.clipMeta) return null

    setIsEditing(true)
    return goToClipEdit(data.clipMeta)
  }

  const handleClickDelete = async () => {
    if (!login) {
      goToLogin()
      return null
    }

    return openDeleteModal()
  }

  const handleClickViewer = useCallback(() => {
    if (!typeEncoding) {
      return goToClipViewer(clip.videoId, clip.recId)
    }
    return null
  }, [typeEncoding, clip, goToClipViewer])

  const updateClipCache = async () => {
    const { data } = await getClipMeta()
    if (data?.clipMeta) {
      const clipMetaJson = JSON.parse(data.clipMeta) || {}

      client.cache.modify({
        id: client.cache.identify({ __typename: 'ClipItem', id: clip.videoId }),
        fields: {
          thumbnail: () => clipMetaJson?.thumbnail,
          placeName: () => clipMetaJson?.places?.[0]?.title,
          address: () => clipMetaJson?.places?.[0]?.address,
        },
      })
    }
  }

  const visibilitychangeHandler = useCallback(() => {
    if (document.visibilityState === 'visible' && isEditing) {
      updateClipCache()
      setIsEditing(false)
    }
  }, [isEditing])

  useEffect(() => {
    window.addEventListener('visibilitychange', visibilitychangeHandler)
    return () => {
      window.removeEventListener('visibilitychange', visibilitychangeHandler)
    }
  }, [visibilitychangeHandler])

  return (
    <li className={classNames(styles.list_item, { [styles.type_encoding]: typeEncoding })} ref={ref}>
      <div className={styles.list_item_inner}>
        <Clickable className={styles.thumb_area} onClick={handleClickViewer} nClickCode={nClicks.reviewTab.CLIP_THUMB}>
          <div className="place_thumb">
            {isImageBlocked ? (
              <FallbackImage width={140} height={233} />
            ) : (
              <Image src={thumbnail + '?type=f90_160@3x'} width="100%" height={233} alt="" className={styles.thumb} />
            )}
          </div>
          {isEncodingFailed && (
            <div className={styles.txt_area}>
              <CommonSuspendedIcon className={styles.icon_encoding_failed} aria-hidden="true" width={50} height={50} />
              <div className="place_blind">인코딩 실패</div>
            </div>
          )}
          {isEncoding && (
            <div className={styles.txt_area}>
              <Image src={ClipEncodingImage} alt="" className={styles.img_encoding} />
              <div className={styles.txt}>인코딩 중</div>
            </div>
          )}
          {typeBlocked && (
            <div className={styles.txt_area}>
              <CommonSuspendedIcon className={styles.icon_encoding_failed} aria-hidden="true" width={50} height={50} />
              <div className="place_blind">검수 실패</div>
            </div>
          )}
          {!typeEncoding && !typeBlocked && (
            <div className={styles.place_info_area}>
              <span className={styles.address_area}>{getRegionName(clip.address)}</span>
              <div className={styles.tit_area}>
                <span className={styles.name}>{clip.placeName}</span>
              </div>
            </div>
          )}
        </Clickable>
        {isMyPage && !isEncoding && (
          <div className={styles.context_menu_wrapper}>
            <ContextMenu
              button={{
                alt: '클립 관리',
                styleClass: styles.btn_manage,
                iconStyleClass: styles.icon,
              }}
              layerStyleClass={styles.ly_manage}
              items={menuItems}
            />
          </div>
        )}
      </div>
    </li>
  )
}

const ClipItemCard = {
  fragments: {
    userClips: gql(`
      fragment ClipItemCard_UserClips on ClipItem {
        id
        videoId
        thumbnail
        description
        encodingStatusCode
        address
        placeName
        recId
        blockedYn
        imageBlockedYn
      }
    `),
  },
  component: React.forwardRef(ClipCard),
}

export default ClipItemCard
